import { useMemo, useState } from 'react';
import { Alert, Box, Button, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useConfig } from '../../components/Configuration';
import { numberFormatted } from '../../Formatters';
import { SubscriptionModel } from '../../interfaces';
import { currentGuidesGiven, describeContent, describeGiven, useSubscriptionGuides } from '../content';

const PageSize = 50;

export function ContentPanel({ subscription, onChange }: { subscription: SubscriptionModel; onChange(): void }) {
  const config = useConfig();
  const { guides, loading, error } = useSubscriptionGuides();
  const [filter, setFilter] = useState('');
  const [shown, setShown] = useState(PageSize);
  const { content, product } = subscription;

  const given = useMemo(() => (guides ? currentGuidesGiven(content, product, guides) : []), [guides, content, product]);
  const matching = useMemo(() => {
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return given.filter((g) => words.every((w) => `${g.number} ${g.title}`.toLowerCase().includes(w)));
  }, [given, filter]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">{describeContent(content, product)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {!content
              ? 'Abonnementet gir ingen anvisninger ennå.'
              : product && guides
                ? describeGiven(content, product, guides)
                : content.includesOldVersions
                  ? 'Med historiske anvisninger.'
                  : 'Bare gjeldende versjoner.'}
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={{ alignItems: { sm: 'flex-end' } }}>
          <Button variant="outlined" size="small" startIcon={<Edit />} disabled={!product} onClick={onChange} sx={{ whiteSpace: 'nowrap' }}>
            Endre innhold
          </Button>
          {!product && (
            <Typography variant="caption" color="text.secondary">
              Velg produkt under Detaljer først.
            </Typography>
          )}
        </Stack>
      </Stack>

      {content && product && (
        <Box sx={{ mt: 3 }}>
          {loading && <LoadingSpinner />}
          {error && <Alert severity="error">Fikk ikke hentet anvisningene fra Fag, så de kan ikke listes nå.</Alert>}
          {guides && (
            <>
              <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">{numberFormatted(given.length) ?? '0'} gjeldende anvisninger</Typography>
                <TextField
                  size="small"
                  placeholder="Søk på nummer eller tittel"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setShown(PageSize);
                  }}
                  sx={{ width: { xs: '100%', sm: 280 } }}
                />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nummer</TableCell>
                    <TableCell>Tittel</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Serie</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matching.slice(0, shown).map((g) => (
                    <TableRow key={g.docId}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {/* The portal's /dokument/{docId} sends on to the guide's own address. */}
                        {config?.portalUrl ? (
                          <Link href={`${config.portalUrl}/dokument/${g.docId}`} target="_blank" rel="noreferrer" underline="hover">
                            {g.number}
                          </Link>
                        ) : (
                          g.number
                        )}
                      </TableCell>
                      <TableCell>{g.title}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'text.secondary' }}>{g.series ?? '–'}</TableCell>
                    </TableRow>
                  ))}
                  {matching.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                        {given.length === 0 ? 'Ingen gjeldende anvisninger.' : 'Ingen treff.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {matching.length > shown && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button size="small" onClick={() => setShown(shown + PageSize)}>
                    Vis flere ({numberFormatted(matching.length - shown)} til)
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
